"use client"

import type React from "react"
import { useState } from "react"
import { useLocation } from "react-router";
import { useNavigate } from "react-router";
import { useSchoolLayoutStore } from "@/hooks/stores/schoolLayoutStore";
import {
  LayoutDashboard,
  GraduationCap,
  User,
  Settings,
  LogOut,
  Search,
  Book,
  Goal,
  FolderOpen,
  BarChart3,
  Trophy,
  Brain,
  ClipboardList,
  Menu,
  X,
  Building,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  StickyNote,
  Gamepad2,
  Settings2,
  BookUser,
  GitGraph,
  Pen,
  Table,
  File
} from "lucide-react"

interface NavItem {
  icon: React.ReactNode
  label: string
  href: string
  onClick?: () => void
}

interface SidebarProps {
  onNotesClick?: () => void
}

// Main Sidebar component - self-contained version
export function SchoolSidebar({ onNotesClick }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  // Collapse state lives in a shared store so all pages can read it
  const { isCollapsed, toggle: toggleCollapse } = useSchoolLayoutStore()

  const mainNavItems: NavItem[] = [
    {
      icon: <LayoutDashboard size={20} />,
      label: "Dashboard",
      href: "/school-dashboard",
    },
    {
      icon: <GraduationCap size={20} />,
      label: "All Students",
      href: "/students-management",
    },
    {
      icon: <Building size={20} />,
      label: "Classes",
      href: "/class-management",
    },
    {
      icon: <BookUser size={20} />,
      label: "Subjects",
      href: "/subject-management",
    },
    {
      icon: <FolderOpen size={20} />,
      label: "Resources",
      href: "/school-resources",
    },
    {
      icon: <Book size={20} />,
      label: "Assessments",
      href: "/assessment-management",
    },
    {
      icon: <Settings2 size={20} />,
      label: "Staff & Admins",
      href: "/admin-management",
    },
    {
      icon: <BookUser size={20} />,
      label: "Mentors",
      href: "/mentor-management",
    },
    {
      icon: <Pen size={20} />,
      label: "Events",
      href: "/event-management",
    },
    {
      icon: <GitGraph size={20} />,
      label: "Analytics",
      href: "/analytics",
    },
    {
      icon: <File size={20} />,
      label: "Reports Center",
      href: "/reports-center",
    },
    {
      icon: <Table size={20} />,
      label: "Data Center",
      href: "/data-center",
    },
  ]

  const settingsItems: NavItem[] = [
    // {
    //   icon: <User size={16} />,
    //   label: "Profile",
    //   href: "#",
    // },
    {
      icon: <Settings size={16} />,
      label: "Settings",
      href: "/school-settings",
    },
    {
      icon: <LogOut size={16} />,
      label: "Log out",
      href: "/#",
    },
  ]

  const handleNavigation = (href: string, onClick?: () => void) => {
    if (onClick) {
      onClick()
    } else {
      console.log("[Navigating to:", href)
      navigate(href)
    }
    setIsMobileOpen(false)
    setIsSettingsOpen(false)
  }

  const toggleSidebar = () => {
    toggleCollapse()
    setIsSettingsOpen(false)
  }

  const toggleSettings = () => {
    setIsSettingsOpen(!isSettingsOpen)
  }

  const handleNotesClick = () => {
    if (onNotesClick) {
      onNotesClick()
      setIsMobileOpen(false)
    }
  }

  // FIXED: Better isActive function with trimmed paths and exact matching
  const isActive = (href: string) => {
    const cleanHref = href.trim()
    const cleanPathname = pathname.trim()

    // For exact matching
    if (cleanHref === cleanPathname) return true

    // Special case for dashboard
    if (cleanHref === "/school-dashboard" && cleanPathname === "/") return true

    // For partial matching (if needed)
    // if (cleanPathname.startsWith(cleanHref) && cleanHref !== "/") return true

    return false
  }

  const isSettingsActive = settingsItems.some(item => isActive(item.href))

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 shadow-sm"
      >
        {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Desktop collapse button */}
      <button
        onClick={toggleSidebar}
        className="hidden lg:flex items-center justify-center w-8 h-8 bg-transparent border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-all duration-300 z-40 backdrop-blur-md"
        style={{
          position: 'fixed',
          top: '1rem',
          left: isCollapsed ? '4.5rem' : '14.5rem',
          transition: 'left 0.3s ease'
        }}
      >
        {isCollapsed ? <ChevronRight className="text-teal-500" size={24} /> : <ChevronLeft className="text-teal-500" size={24} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen bg-slate-50 border-r border-gray-200 flex flex-col transition-all duration-300 z-40 ${isCollapsed ? "w-16" : "w-56"
          } ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
      >
        {/* Sidebar content wrapper */}
        <div className="flex-1 flex flex-col overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-teal-400 [&::-webkit-scrollbar-track]:bg-gray-100">

          {/* Logo */}
          <div className={`flex items-center ${isCollapsed ? "justify-center p-3" : "gap-2 p-8"}`}>
            <div className={`rounded-lg flex items-center justify-center overflow-hidden ${isCollapsed ? "w-8 h-8" : "w-48 h-16"
              }`}>
              <img
                src="./navlogo.png"
                alt="EarlyScape Logo"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Main navigation */}
          <nav className="flex-1 px-4 py-2">
            <div className="mb-6">
              {/* Section title - hidden when collapsed */}
              {!isCollapsed && (
                <p className="text-xs font-semibold text-gray-500 mb-3 px-2 uppercase tracking-widest">Menu</p>
              )}
              <ul className="space-y-1">
                {mainNavItems.map((item) => {
                  const active = isActive(item.href)
                  return (
                    <li key={item.label}>
                      <button
                        onClick={() => handleNavigation(item.href, item.onClick)}
                        className={`w-full flex items-center rounded-lg text-sm font-medium transition-all duration-200 ${isCollapsed ? "justify-center p-2" : "gap-3 px-4 py-3"
                          } ${active
                            ? "bg-teal-50 text-teal-700 border border-teal-200 shadow-sm"
                            : "text-gray-700 hover:bg-gray-50 border border-transparent"
                          }`}
                        title={isCollapsed ? item.label : ""}
                      >
                        <span className={active ? "text-teal-600" : "text-gray-500"}>
                          {item.icon}
                        </span>
                        {!isCollapsed && (
                          <>
                            <span className="flex-1 text-left">{item.label}</span>
                            {active && <div className="w-2 h-2 rounded-full bg-teal-600"></div>}
                          </>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>

            {/* Settings Dropdown Section */}
            <div>
              {/* Section title - hidden when collapsed */}
              {!isCollapsed && (
                <p className="text-xs font-semibold text-gray-500 mb-3 px-2 uppercase tracking-widest">Account</p>
              )}

              {/* Settings Dropdown */}
              <div className="space-y-1">
                {/* Settings Toggle Button */}
                <button
                  onClick={toggleSettings}
                  className={`w-full flex items-center rounded-lg text-sm font-medium transition-all duration-200 ${isCollapsed ? "justify-center p-2" : "gap-3 px-4 py-3 justify-between"
                    } ${isSettingsActive
                      ? "bg-teal-50 text-teal-700 border border-teal-200 shadow-sm"
                      : "text-gray-700 hover:bg-gray-50 border border-transparent"
                    }`}
                  title={isCollapsed ? "Settings" : ""}
                >
                  <div className="flex items-center gap-3">
                    <span className={isSettingsActive ? "text-teal-600" : "text-gray-500"}>
                      <Settings size={20} />
                    </span>
                    {!isCollapsed && (
                      <span className="flex-1 text-left">Settings</span>
                    )}
                  </div>
                  {!isCollapsed && (
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 transition-transform duration-200 ${isSettingsOpen ? "rotate-180" : ""
                        }`}
                    />
                  )}
                </button>

                {/* Settings Dropdown Menu */}
                {!isCollapsed && isSettingsOpen && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
                    {settingsItems.map((item) => {
                      const active = isActive(item.href)
                      return (
                        <button
                          key={item.label}
                          onClick={() => handleNavigation(item.href)}
                          className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${active
                              ? "bg-teal-50 text-teal-700 border border-teal-200 shadow-sm"
                              : "text-gray-700 hover:bg-gray-50 border border-transparent"
                            }`}
                        >
                          <span className={active ? "text-teal-600" : "text-gray-500"}>
                            {item.icon}
                          </span>
                          <span className="flex-1 text-left">{item.label}</span>
                          {active && <div className="w-2 h-2 rounded-full bg-teal-600"></div>}
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* Collapsed Settings Items */}
                {isCollapsed && (
                  <>
                    {settingsItems.map((item) => {
                      const active = isActive(item.href)
                      return (
                        <button
                          key={item.label}
                          onClick={() => handleNavigation(item.href)}
                          className={`w-full flex items-center justify-center p-2 rounded-lg text-sm font-medium transition-all duration-200 ${active
                              ? "bg-teal-50 text-teal-700 border border-teal-200 shadow-sm"
                              : "text-gray-700 hover:bg-gray-50 border border-transparent"
                            }`}
                          title={item.label}
                        >
                          <span className={active ? "text-teal-600" : "text-gray-500"}>
                            {item.icon}
                          </span>
                        </button>
                      )
                    })}
                  </>
                )}
              </div>
            </div>
          </nav>
        </div>
      </aside>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setIsMobileOpen(false)} />
      )}
    </>
  )
}

// Content wrapper component that adjusts based on sidebar state
export function Content({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 lg:ml-64 transition-all duration-300">
      {children}
    </div>
  )
}